import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInterval } from "react-use";
import { observer } from "mobx-react-lite";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import Spinner from "react-bootstrap/Spinner";
import _groupBy from "lodash/groupBy";
import socketIOClient from "socket.io-client";
import { useTranslation } from "react-i18next";
import { getSportIcon, getSportIconColor } from "../../utils/events";
import { SPORT_ORDER } from "../../utils/constants";
import { EventsModal } from "../EventsModal/EventsModal";
import protobuf from "protobufjs";

import { NavBarMain, ValuebetEventCard, ValuebetCard, CalculatorCard } from "..";
import {
  UserBetsContainer,
  Container,
  SelectedEvent,
  AllArbsContainer,
  SelectedArbContainer,
  VisitorBadge,
  ReportButton,
  SpinnerWrapper,
  ConfigureButton,
  TopBar,
  LeftBar,
  RightBar,
  EventsContainer,
  ConnectionBadge,
  SportIcon,
} from "./Valuebets.styled";
import { ConfigureValuebetModal } from "../ConfigureValuebetModal/ConfigureValuebetModal";
import { Button, Alert } from "react-bootstrap";
import { useSettings, useEvents, useMemoPrev, useUser, useReport } from "../../hooks";
import { SettingsProvider } from "../../contexts";
import { checkIfTokenStillValid } from "../../repositories/utils";
import { ReportProvider } from "../../contexts";
import { getSubscriptionsForValuebets, subscriptionName } from "../../utils/subscriptions";

const fpPromise = FingerprintJS.load({ monitoring: false });
let socket;

const root = await protobuf.load('/protobuf/event.proto');
const EventMessage = root.lookupType('betsmarterpackage.EventMessage');


const useCalculator = () => {
  const [inputReturn, setInputReturn] = useState(200);
  const [calculatorSelectedBookie, setCalculatorSelectedBookie] = useState(undefined);
  const [calculatorEnabled, enableCalculator] = useState(false);
  const [mode, setMode] = useState('normal');

  const getBet1 = (arb) => calculatorSelectedBookie && arb.bets.find(bet => bet.bookie === calculatorSelectedBookie) ? arb.bets.find(bet => bet.bookie === calculatorSelectedBookie) : arb.bets[0];
  const getBet2 = (arb) => calculatorSelectedBookie && arb.bets.find(bet => bet.bookie === calculatorSelectedBookie) ? arb.bets.find(bet => bet.bookie !== calculatorSelectedBookie) : arb.bets[1];

  const getReturnForBet1 = arb => (getBet1(arb).odds * inputReturn) - (mode === 'freebet' ? inputReturn : 0);

  const getStakeForBet2 = arb => getReturnForBet1(arb) / getBet2(arb).odds;

  const getStakeForBet = (arb, { bookie, idx }) => bookie === calculatorSelectedBookie || (!calculatorSelectedBookie && idx === 0) ? inputReturn : getStakeForBet2(arb);

  return { inputReturn, setInputReturn, calculatorSelectedBookie, setCalculatorSelectedBookie, calculatorEnabled, enableCalculator, getBet1, getBet2, getReturnForBet1, getStakeForBet, getStakeForBet2, mode, setMode };
};

// Wrap into provider:
// - configuration provider to have access to configuration
// - report provider to have a single modal for all reports
export const Valuebets = (props) => (
  <SettingsProvider type="valuebetsLive" >
    <ReportProvider>
      <ArbsContent {...props} />
    </ReportProvider>
  </SettingsProvider>
);

const ArbsContent = observer(({ history }) => {
  const { t } = useTranslation();
  const { captureSnapshot, getEvents } = useEvents();
  const { settings } = useSettings();
  const configuration = settings?.configuration;
  const [eventFiltered, setEventState] = useState(undefined);
  const [showConfigurationModal, setShowConfigurationModal] = useState(false);
  const [pinnedValuebetId, setPinnedValuebetId] = useState(undefined);
  const { getActiveSubscription, getExtensionConfiguration } = useUser();
  const [subscriptionStatus, setSubscriptionStatus] = useState(undefined);
  const [filters, setFilters] = useState([]);
  const [visitorId, setVisitorId] = useState(undefined);
  const [tokenConfiguration, setTokenConfiguration] = useState(undefined);
  const [showVisitorId] = useState(false);
  const { openReportModal } = useReport();
  const [data, setData] = useState({ valuebets: {}, events: {} });
  const [areValuebetsLoaded, setAreValuebetsLoaded] = useState(false);
  const [connected, setConnected] = useState(false);
  const [eventsFromAPI, setEventsFromAPI] = useState("{}");
  const [showEventModal, setShowEventModal] = useState(false);

  const calculator = useCalculator();

  const processFilter = useCallback((filter, valuebet) => {
    const filterTypes = {
      1: () => filter.data.id === valuebet.id,
      2: () => filter.data.eventId === valuebet.match.id,
      3: () =>
        filter.data.eventId === valuebet.match.id && [valuebet.bet.bookie].includes(filter.data.bookie),
    };

    return filterTypes[filter.type] ? filterTypes[filter.type]() : false;
  }, []);

  const checkIfNeedsFiltering = useCallback(
    (arb) => !filters.some((filter) => processFilter(filter, arb)),
    [filters, processFilter]
  );

  useEffect(() => {
    calculator.setMode(configuration?.mode);
  }, [configuration?.mode]);

  // A function that sorts arbs depending on the configuration
  const arbsSortFunc = useMemo(() => {
    const sortBy = {
      timeAsc: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      timeDes: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      profitDes: (a, b) => b.rawProfit - a.rawProfit,
      profitAsc: (a, b) => a.rawProfit - b.rawProfit,
    };

    return sortBy.profitDes;
  }, [configuration?.sortBy, configuration?.mode]);

  const checkIfPaused = useCallback((bet) => data.events[bet.event.id].status.paused, [data]);

  // filter arbs using the selected settings configuration
  const filteredValuebets = useMemo(
    () => {
      // Filter the valuebets object while maintaining object structure
      return Object.entries(data.valuebets).reduce((filtered, [id, valuebet]) => {
        // Apply filters
        let bookieConfig = configuration.subscriptionConfigs[valuebet.bet.bookie];
        if (bookieConfig === undefined) bookieConfig = configuration;

        // if (!checkIfNeedsFiltering(valuebet)) return filtered;

        // console.log(valuebet);

        // Only include selected bookies
        if (!configuration.bookies.some(bookie => bookie.includes(valuebet.bet.bookie))) return filtered;

        // Filter minPercentage
        if (!(valuebet.value.valueBetPercentage >= (parseInt(bookieConfig.minPercentage) || 0))) return filtered;

        // Filter maxPercentage
        if (!(valuebet.value.valueBetPercentage <= (parseInt(bookieConfig.maxPercentage) || 100))) return filtered;

        // Filter sports
        if (!configuration.sports.includes(valuebet.event?.sport?.name)) return filtered;

        // Filter sports markets
        if (!configuration.sportsMarkets[valuebet.event?.sport?.name]?.includes(valuebet.bet.marketShortName))
          return filtered;

        // Filter excluded leagues
        if (configuration.excludedLeagues?.some((league) =>
            valuebet.event?.league?.name.toLowerCase().includes(league.toLowerCase())
          )
        ) return filtered;

        const oddsInsideFilter = (bookieConfig.minOdds <= valuebet.bet.odds && bookieConfig.maxOdds >= valuebet.bet.odds);
        if(!oddsInsideFilter) return filtered;

        // Filter minTime
        const isInTimeRange = (new Date() - valuebet.internalTimestamp > parseInt(bookieConfig.minTime) * 1000 &&
          new Date() - valuebet.internalTimestamp < parseInt(bookieConfig.maxTime) * 1000);
          
        if(!isInTimeRange) return filtered;

        const isPaused = bookieConfig.pausedSports && bookieConfig.pausedSports.includes(valuebet.event?.sport?.name) ? checkIfPaused(valuebet) : true;
        if (!isPaused) return filtered;

        // If all filters passed, keep the valuebet
        filtered[id] = valuebet;
        return filtered;
      }, {});
    },
    [data, checkIfNeedsFiltering, eventFiltered, configuration]
  );

  const pinnedValuebet = useMemoPrev((prevpinnedValuebet) => {
    if (pinnedValuebetId === undefined) return undefined;
    let valuebet = Object.values(data.valuebets).find(valuebet => valuebet.id === pinnedValuebetId);
    if (!valuebet && prevpinnedValuebet) return {...prevpinnedValuebet, deleted: true};
    return valuebet;
  }, [pinnedValuebetId, data])

  const setPinnedValuebet = useCallback((valuebet) => {
    calculator.setCalculatorSelectedBookie(undefined);
    return setPinnedValuebetId(valuebet?.id)
  }, [setPinnedValuebetId]);

  // array of matches obtained from grouping all arbs by its match id
  const events = useMemo(() => {
    const eventsHash = _groupBy(filteredValuebets, (valuebet) => valuebet.event.id);
    const _events = Object.entries(eventsHash).map(([eventId, eventValuebets]) => ({
      id: parseInt(eventId),
      valuebets: eventValuebets.sort(arbsSortFunc),
    }));
    return _events.filter(({ id }) => (eventFiltered ? eventFiltered.id === id : true)).sort((eventA, eventB) => arbsSortFunc(eventA.valuebets[0], eventB.valuebets[0]));
  }, [filteredValuebets]);

  const eventsForPinnedValuebet = useMemo(
    () => (pinnedValuebet ? events.filter((event) => event.id === pinnedValuebet.event.id) : []),
    [events, pinnedValuebet]
  );

  const createReport = useCallback(async (event) => {
    event.stopPropagation();
    openReportModal();
  }, [openReportModal]);

  const setEvent = useCallback((event) => {
    if (!eventFiltered) setEventState(event);
  }, []);

  const addFilter = useCallback((filter) => setFilters((previousFilters) => [...previousFilters, filter]), []);

  // Inital stuff to do after first render
  useEffect(() => {
    checkIfTokenStillValid(history);

    // Use a function to use async/await syntax inside useEffect
    const onMounted = async () => {
      await Promise.all([
        getActiveSubscription().then((subscriptionStatus) => setSubscriptionStatus(subscriptionStatus)),
        fpPromise.then((fp) => fp.get()).then((result) => setVisitorId(result.visitorId)),
        getExtensionConfiguration().then((response) => setTokenConfiguration(response)),
      ]);
    };
    onMounted();

  }, []);

  // useEffect(() => {
  //   let timeoutId;
  //   let stopped = false;

  //   const processUpdates = () => {
  //     // Periodically get new arbs
  //       // Do nothing until settings are loaded
  //       if (!settings) return;
    
  //       const { configuration } = settings;
  //       const { minPercentage, maxPercentage, minOdds, maxOdds } = configuration;
  //       const channels = getSubscriptionsForValuebets(configuration);
  //       const fn = getValuebetsPre;

  //       fn({ channels, minPercentage, maxPercentage, minOdds, maxOdds }).then(newValuebets => {
  //         setAreValuebetsLoaded(true);
  //         setValuebets(newValuebets);
  //         if (!stopped) timeoutId = window.setTimeout(processUpdates, 2000);
  //       });
  //     };
    
  //     processUpdates();

  //     return () => {
  //       stopped = true;
  //       window.clearTimeout(timeoutId);
  //     };
  // }, [settings]); 

    // Do stuff once the settings are loaded
    useEffect(() => {
      // Do not run until settings are loaded
      if (!settings) return;
      // Do not run twice
      if (socket !== undefined) return;
      
      // const websocketURL = subscriptionStatus?.status === "NON_EXISTING" ? "https://wsfree.betsmarter.app/" : "https://ws.betsmarter.app/";
      const websocketURL = "https://ws.betsmarter.app/";
  
      socket = socketIOClient(websocketURL, {
        path: "/valuebets",
        transports: ["websocket"],
        auth: { token: `Bearer ${localStorage.jwt}` },
      });
  
      // Handling token expiration
      socket.on("connect_error", (error) => {
        if (error.message === "not_subscribed") {
          console.error("User not subscribed!");
        } else if (error.type === "TransportError") {
          console.error("Connection lost");
        } else if ((error.data || {}).type === "UnauthorizedError") {
          console.error("User token has expired");
        }
      });
  
      socket.on("connect", () => {
        console.log("Connected to websocket");
        setConnected(true);
  
        // const performance = navigator?.connection || {};
  
        // const subscriptions = getSubscriptionsForSocket(configuration);
        // socket.send({ subscribe: subscriptions });
        // socket.send({
        //   info: {
        //     id: visitorId,
        //     performance: { downlink: performance.downlink, effectiveType: performance.effectiveType },
        //   },
        // });
      });
  
      socket.on("disconnect", () => {
        setConnected(false);
      });
  
      let updates = [];
  
      socket.on("set", (payload) => {
        // const decoded = ArbMessage.decode(new Uint8Array(payload));
        updates.push(payload);
      });
  
      // socket.on("warning", (payload) => {
      //   if (payload.type === "fingerprint") {
      //     setFingerprintDetected(true);
      //   }
      // });
  
      socket.on("removed", (payload) => {
        updates.push(payload);
      });
  
      socket.on("eventsUpdate", (payload) => {
        setEventsFromAPI(JSON.stringify(payload.data));
      });
  
      socket.on("eventUpdate", (payload) => {
        const decoded = EventMessage.decode(new Uint8Array(payload));
        updates.push({ type: "eventUpdate", data: decoded });
      });
  
      // Process updates periodically to update arbs
      const processUpdates = () => {
        const updatesCopy = [...updates];
        updates.splice(0, updatesCopy.length); // clear array
  
        setData((previousData) => {
          const newData = { ...previousData };
          updatesCopy.forEach(({ type, data }) => {
            try {
              if (type === "set" || type === "deleted") {
                const { bet } = data;
                const id = bet.internalTopic || bet.topic;
                const timestamp = new Date(data.timestamp);

                if (type === 'set') {
                  newData.events[data.event.id] = { ...data.event, status: data.status };
                }
  
                const isLatestUpdate = newData.valuebets[id] ? timestamp > newData.valuebets[id].timestamp : true;
  
                if (isLatestUpdate) {
                  // if ((newValuebets[id] || {}).type === "deleted" && type === "deleted") {
                  if (type === "deleted") {
                    delete newData.valuebets[id];
                  } else {
                    const internalTimestamp = new Date();
                    const previousValuebet = (newData.valuebets[id] || {}).type === "set" ? newData.valuebets[id].data : (newData.valuebets[id] || {}).previousValuebet;
                    if  (data.event && data.value.valueBetPercentage) newData.valuebets[id] = { type, id, version: data.version, internalTimestamp, bet: { ...data.bet, id, internalTimestamp }, event: data.event, value: data.value, timestamp, previousValuebet, status: data.status };
                  }
                }
  
                // if (type === "added") {
                //   newValuebets.events[data.match.id] = { ...data.match, status: data.status };
                // }
              }
              else if (type === "eventUpdate") {
                newData.events[data.id] = data;
              } 
          } catch (error) {
            console.error(error);
          }
          });
          return newData;
        });
  
        window.setTimeout(processUpdates, 500);
        setAreValuebetsLoaded(true);
      }
  
      processUpdates();
  
      // Fetch events
      getEvents().then((events) => setEventsFromAPI(JSON.stringify(events)));
    }, [settings]);

  return (
    <>
      <UserBetsContainer>
        <NavBarMain currentPage="valuebets" type="valuebetsLive" history={history}/>
        {/* {configuration?.bookiesReference?.length === 0 && (
          <Alert variant="danger" className="customAlert">
            <p>No tienes ninguna bookie de referencia seleccionada. ¡Modifica tu configuración!</p>
          </Alert>
        )} */}
        <rn-banner />
        {/* <div style={{ display: "flex", alignItems: "center", marginTop: '20px' }}>
          <ConfigureValuebetModal show={showConfigurationModal} setVisible={setShowConfigurationModal} />
          <ConfigButton disabled={!configuration} onClick={() => setShowConfigurationModal(true)}>
            Configurar
          </ConfigButton>
          <ReportButton onClick={createReport}>
            <span className="fas fa-exclamation-triangle"></span>
            <a>Reportar</a>
          </ReportButton>
          {showVisitorId && <VisitorBadge>{visitorId}</VisitorBadge>}
        </div> */}
          <TopBar>
          <LeftBar>
          <ConfigureValuebetModal show={showConfigurationModal} setVisible={setShowConfigurationModal} />
            <ConfigureButton
              disabled={!(connected && configuration)}
              onClick={() => setShowConfigurationModal(true)}
          >
            {t("configure")}
          </ConfigureButton>
          {Object.values(JSON.parse(eventsFromAPI)).length > 0 && (
            <EventsContainer onClick={() => setShowEventModal(true)}>
              {Object.entries(JSON.parse(eventsFromAPI))
                .sort(([sportA], [sportB]) => SPORT_ORDER.indexOf(sportA) - SPORT_ORDER.indexOf(sportB))
                .map(([sport, events]) => (
                  <a key={sport} style={{ marginRight: "10px", textTransform: "Capitalize", display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <SportIcon className="sport" src={getSportIcon(sport)} filter={getSportIconColor(sport)} />
                    <span className="badgeCustom badge badge-light">{events.length}</span>
                  </a>
                ))}
            </EventsContainer>
          )}
          {/* <ConnectionBadge connected={connected} onClick={() => setShowVisitorId(!showVisitorId)}>
            {connected ? t("connected") : t("notConnected")}
            </ConnectionBadge> */}
            {showVisitorId && <VisitorBadge>{visitorId}</VisitorBadge>}
          </LeftBar>
          <RightBar>
            <ReportButton onClick={createReport}>
              <span className="fas fa-exclamation-triangle"></span>
              <a>Reportar</a>
            </ReportButton>
          </RightBar>
        </TopBar>
        {configuration &&
          (!areValuebetsLoaded ? (
            <SpinnerWrapper>
              <Spinner animation="border" role="loading" />
            </SpinnerWrapper>
          ) : (
            <>
              {eventFiltered && (
                <SelectedEvent>
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    style={{ float: "left", marginRight: "5px" }}
                    onClick={() => setEventState(undefined)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                  Evento seleccionado:
                  <span className="badge badge-secondary" style={{ fontSize: "medium" }}>
                    {eventFiltered.name}
                  </span>
                </SelectedEvent>
              )}
              <EventsModal show={showEventModal} setVisible={setShowEventModal} events={JSON.parse(eventsFromAPI)} />
              <Container>
                <AllArbsContainer pinnedValuebet={!!pinnedValuebet}>
                  {events.map((event) => (
                    <ValuebetEventCard
                      key={event.id}
                      configuration={configuration}
                      valuebets={event.valuebets}
                      event={data.events[event.id]}
                      // event={event.valuebets[0].event}
                      captureSnapshot={captureSnapshot}
                      setEvent={setEvent}
                      setPinnedValuebet={setPinnedValuebet}
                      eventFiltered={eventFiltered}
                    />
                  ))}
                </AllArbsContainer>
                {pinnedValuebet && (
                  <SelectedArbContainer pinnedValuebet={!!pinnedValuebet}>
                    <ValuebetCard
                      valuebet={pinnedValuebet}
                      event={pinnedValuebet.event}
                      captureSnapshot={captureSnapshot}
                      pinned={true}
                      setPinnedValuebet={setPinnedValuebet}
                      addFilter={addFilter}
                      calculator={calculator}
                      tokenConfiguration={tokenConfiguration}
                    />
                    <CalculatorCard
                      valuebet={pinnedValuebet}
                      event={pinnedValuebet.event}
                      pinned={true}
                      calculator={calculator}
                    />
                    {eventsForPinnedValuebet.map((event) => (
                    <ValuebetEventCard
                        key={event.id}
                        configuration={configuration}
                        valuebets={event.valuebets}
                        event={data.events[event.id]}
                        captureSnapshot={captureSnapshot}
                        setEvent={setEvent}
                        setPinnedValuebet={setPinnedValuebet}
                        eventFiltered={true}
                        pinned={true}
                      />
                    ))}
                  </SelectedArbContainer>
                )}
              </Container>
            </>
          ))}
      </UserBetsContainer>
    </>
  );
});
